import { memo, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CheckOutlined, ContentCopyOutlined, DownloadOutlined, KeyOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

type MfaBackupCodesDialogProps = {
  backupCodes: string[];
  onClose: () => void;
};

const MfaBackupCodesDialog = ({ backupCodes, onClose }: MfaBackupCodesDialogProps) => {
  const { t } = useTranslation('authentication');
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyCodes = async () => {
    await navigator.clipboard?.writeText(backupCodes.join('\n'));
    setCopied(true);
  };

  const downloadCodes = () => {
    const url = URL.createObjectURL(new Blob([backupCodes.join('\n')], { type: 'text/plain' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'mfa-backup-codes.txt';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open maxWidth='sm' fullWidth onClose={acknowledged ? onClose : undefined} PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>
        <Stack direction='row' spacing={1.5} alignItems='center'>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'warning.main',
              bgcolor: ({ palette }) => alpha(palette.warning.main, 0.14),
            }}
          >
            <KeyOutlined fontSize='small' />
          </Box>
          <span>{t('mfa.backupCodes.title')}</span>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant='body2' color='text.secondary' mb={2.5}>
          {t('mfa.backupCodes.description')}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1,
            p: 2,
            borderRadius: 2,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
        >
          {backupCodes.map((backupCode) => (
            <Typography
              key={backupCode}
              component='code'
              sx={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: 1, textAlign: 'center', py: 0.5 }}
            >
              {backupCode}
            </Typography>
          ))}
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={2}>
          <Button
            variant='outlined'
            color='inherit'
            startIcon={copied ? <CheckOutlined color='success' /> : <ContentCopyOutlined />}
            onClick={copyCodes}
          >
            {t(copied ? 'mfa.backupCodes.copied' : 'mfa.backupCodes.copy')}
          </Button>
          <Button variant='outlined' color='inherit' startIcon={<DownloadOutlined />} onClick={downloadCodes}>
            {t('mfa.backupCodes.download')}
          </Button>
        </Stack>

        <FormControlLabel
          sx={{ mt: 2.5 }}
          control={
            <Checkbox checked={acknowledged} onChange={(event) => { setAcknowledged(event.target.checked); }} />
          }
          label={<Typography variant='body2'>{t('mfa.backupCodes.acknowledge')}</Typography>}
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button disabled={!acknowledged} variant='contained' onClick={onClose}>
          {t('mfa.backupCodes.done')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default memo(MfaBackupCodesDialog);
